import SearchIcon from '@mui/icons-material/Search'
import {
  Backdrop,
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { AuthContext } from '../../libs/contexts/authContext'
import { logout } from '../../store/actions/authActions'
import FilterButton from '../FilterButton'
import ApplicationManagementTable from './ApplicationManagementTable'
import {
  Appbox,
  LabelContainer,
  ResponsiveGridBox,
  StatsNumber,
  StatsNumberContainer,
  StyledContainer,
  StyledFilterButtonGrid,
  StyledFilters,
} from './styledElements'
import {
  appsBlobSelector,
  factTableAppsBlobSelector,
  fetchAgentList,
  fetchAgentProducts,
  fetchApps,
  fetchFactTableApps,
  fetchMetrics,
  loadingSelector,
  metricsListSelector,
  productsBlobSelector,
  agentListSelector,
} from './tableSlice'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'

const ApplicationManagement = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { search } = useLocation()
  const loggedIn = useSelector((state) => state.auth.isAuthenticated)
  const relationalType = useSelector((state) => state.auth.relationalType)
  const { apps, appsError } = useSelector(appsBlobSelector)
  const { metricsList } = useSelector(metricsListSelector)
  const { agentList: agentListNoAff } = useSelector(agentListSelector)
  const { factTableApps, factTableAppsError } = useSelector(factTableAppsBlobSelector)
  const { products, getAgentProductsError } = useSelector(productsBlobSelector)
  const loading = useSelector(loadingSelector)
  const { productId, affiliateCode: urlAffiliateCode } = useContext(AuthContext)
  const affiliateCodeState = useSelector((state) => state.auth.affiliateCode)
  const [affiliateCode, setAffiliateCode] = useState(affiliateCodeState)
  const [agentList, setAgentList] = useState([])


  useEffect(() => {
    if (!loggedIn) {
      dispatch(logout())
      navigate(`/login?affiliateCode=${urlAffiliateCode}`)
      return undefined
    }
  }, [affiliateCode, dispatch, loggedIn, navigate, search, urlAffiliateCode])

  const [dayUnit, setDayUnit] = useState('365d')
  const [visibleRows, setVisibleRows] = useState(apps)
  const [visibleMetrics, setVisibleMetrics] = useState((metricsList || []).find((m) => m.product_id === 1) ?? {})
  const [displayingFtapps, setDisplayingFtapps] = useState(false) // boolean flag for displaying fact table apps
  const [searchBy, setSearchBy] = useState('')

  const [filterBy, setFilterBy] = useState({
    appStatus: 'All',
    ticketStatus: 'All',
    productName: 'All',
  })
  const [boxFilters, setBoxFilters] = useState({
    Applications: '',
    'Identity Verified': '',
    'Underwriting Questions Submitted': '',
    Consent: '',
    Approved: '',
    'Policy Submitted': '',
    'Not Paid': '',
    'Policy Issued': '',
  })
  const productInQuestion = products.find((p) => p.productName === filterBy.productName) || {}

  const keyDownHandler = ({ key }) => {
    if (key === 'Enter' && searchBy) {
      setVisibleRows((prev) => prev.filter((r) => r.arcId.includes(searchBy) || String(r.policyNo).includes(searchBy)))
    }
  }

  useEffect(() => {
    if (filterBy.productName === 'All') {
      setVisibleMetrics(metricsList.find((m) => m.product_id === 1))
    } else if (filterBy.productName === 'Quility Level Term and ROP') {
      setVisibleMetrics(metricsList.find((m) => m.product_id === 814))
    } else {
      const found = metricsList.find((m) => String(m.product_id) === productInQuestion.productId) ?? {}
      setVisibleMetrics(found)
    }
  }, [apps, displayingFtapps, factTableApps, filterBy.productName, metricsList, productInQuestion.productId])

  useEffect(() => {
    if (!searchBy || !displayingFtapps) {
      setVisibleRows(apps)
    }
    if (displayingFtapps) {
      setVisibleRows(factTableApps)
    }
  }, [apps, displayingFtapps, factTableApps, searchBy])

  const boxFilterFunc = (key) => {
    setDisplayingFtapps(true)
    const obj = {
      Applications: `last_${dayUnit}_apps_init`,
      'Identity Verified': `last_${dayUnit}_id_check_pass`,
      'Underwriting Questions Submitted': `last_${dayUnit}_uwq_pass`,
      Consent: `last_${dayUnit}_uw_consent`,
      Approved: `last_${dayUnit}_approved_app`,
      'Policy Submitted': `last_${dayUnit}_policy_submitted`,
      'Not Paid': `last_${dayUnit}_missing_payment`,
      'Policy Issued': `last_${dayUnit}_policy_issued`,
    }
    if (boxFilters[key] === '') {
      const prevObj = { ...boxFilters }
      Object.entries(prevObj).forEach(([k, v]) => {
        if (k !== key) {
          prevObj[k] = ''
        }
      })
      setBoxFilters({ ...prevObj, [key]: obj[key] })
    } else {
      setBoxFilters((prev) => ({
        ...prev,
        [key]: '',
      }))
    }
  }

  const handleFilterBy = (name) => (value) => {
    setFilterBy((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // const applyFilters = useCallback(() => {
  //   let newData = null
  //   if (!apps) {
  //     return
  //   }

  //   if (displayingFtapps) {
  //     newData = factTableApps.slice()
  //   } else {
  //     newData = Array.isArray(apps) ? [...apps] : []
  //   }

  //   const currentDate = new Date()
  //   let subtractDays

  //   switch (dayUnit) {
  //     case '7d':
  //       subtractDays = 7
  //       break
  //     case '30d':
  //       subtractDays = 30
  //       break
  //     case '90d':
  //       subtractDays = 90
  //       break
  //     case '365d':
  //       subtractDays = 365
  //       break
  //     default:
  //       subtractDays = 0
  //       break
  //   }

  //   currentDate.setDate(currentDate.getDate() - subtractDays)
  //   newData = newData.filter((application) => new Date(application.applicationDate) >= currentDate)

  //   // newData = newData.filter(boxFilter);
  //   setVisibleRows(newData)
  // }, [apps, displayingFtapps, dayUnit, factTableApps])

  const getMappedData = (dayUnit) => {
    const prefix = `last_${dayUnit}`
    const metricsObj = visibleMetrics ? { ...visibleMetrics } : {}
    return {
      Quotes: metricsObj[`${prefix}_quotes`] || 0,
      Applications: metricsObj[`${prefix}_apps_init`] || 0,
      'Identity Verified': metricsObj[`${prefix}_id_check_pass`] || 0,
      'Underwriting Questions Submitted': metricsObj[`${prefix}_uwq_pass`] || 0,
      Consent: metricsObj[`${prefix}_uw_consent`] || 0,
      Approved: metricsObj[`${prefix}_approved_app`] || 0,
      'Policy Submitted': metricsObj[`${prefix}_policy_submitted`] || 0,
      'Not Paid': metricsObj[`${prefix}_missing_payment`] || 0,
      'Policy Issued': metricsObj[`${prefix}_issued_policies`] || 0,
    }
  }

  const clearAllFilters = () => {
    Object.keys(filterBy).forEach((key) => {
      filterBy[key] = 'All'
    })
    if (displayingFtapps) {
      setDisplayingFtapps(false)
    }
    setVisibleRows(apps)
    setBoxFilters((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, ''])))
  }

  const handleAgentsSearch = (event, value) => {
    if (value != null) {
      setAffiliateCode(value.affCode)
    }
  }

  useEffect(() => {
    let agentNamesAff = []
    if (agentListNoAff.length >= 1) {
      agentNamesAff = agentListNoAff.map((agent) => ({
        label: `${agent.name}`,
        affCode: `${agent.affilateCode}`,
      }))
    }
    setAgentList(agentNamesAff)
  }, [agentListNoAff])

  useEffect(() => {
    const fetchData = () => {
      dispatch(fetchApps({ affiliateCode }))
      dispatch(fetchMetrics({ affiliateCode }))
      dispatch(fetchFactTableApps({ affiliateCode }))
      dispatch(fetchAgentProducts({ affiliateCode }))
    }
    fetchData()
    if (relationalType !== 87) {
      const agentData = {
        distributorId: '8973',
        agencyId: '1',
      }
      dispatch(fetchAgentList(agentData))
    }
  }, [affiliateCode, dispatch])

  const mappedData = getMappedData(dayUnit)
  const constantProducts = productId === "81403" ? ['All'] : ['All', 'Quility Level Term and ROP'];
  const productOptions =  constantProducts.concat(products.map((p) => p.productName))
  const applicationStatusOptions = ['All', 'Completed', 'In Progress', 'Not Started', 'Submitted', 'Expired']
  const ticketStatusOptions = ['All', 'Open', 'In Progress', 'Closed']
  const labels = [
    'Quotes',
    'Applications',
    'Identity Verified',
    'Underwriting Questions Submitted',
    'Consent',
    'Approved',
    'Policy Submitted',
    'Not Paid',
    'Policy Issued',
  ]

  const filterOptions = createFilterOptions({
    limit: 150,
  })

  return (
    <div style={{ backgroundColor: 'rgba(241, 243, 246, 1)', fontFamily: 'Roboto', minHeight: '100vh' }}>
      <Box sx={{ marginTop: 7 }}>
        <Grid container spacing={3} padding="12px">
          <Grid item lg={12} sm={12} xs={12}>
            <Grid container spacing={2} height="100%">
              <Grid item lg={6} md={6} sm={6} xs={12}>
                <Typography align="left" variant="h1" style={{ padding: '24px 14px', fontSize: '24px' }}>
                  Application Management
                </Typography>
              </Grid>
              <Grid
                item
                lg={6}
                md={6}
                sm={6}
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: { lg: 'end', md: 'end', sm: 'end', xs: 'center' },
                  alignItems: 'center',
                }}
              >
                {relationalType === 117 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginRight: { lg: '16px', md: '16px', sm: '16px', xs: '0' },
                    }}
                  >
                    <Autocomplete
                      id="combo-box-demo"
                      options={agentList}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.affCode}>
                            {option.label}
                          </li>
                        )
                      }}
                      onChange={handleAgentsSearch}
                      filterOptions={filterOptions}
                      renderInput={(params) => (
                        <TextField {...params} sx={appManagementStyle.textField} size="small" label="Agents" />
                      )}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: { lg: '8px', md: '8px', sm: '8px', xs: '0' },
                  }}
                >
                  <FilterButton
                    label="Product"
                    options={productOptions}
                    onFilterChange={(selected) => handleFilterBy('productName')(selected)}
                  />
                </Box>
                <ToggleButtonGroup
                  color="primary"
                  value={dayUnit}
                  exclusive
                  onChange={(e, v) => setDayUnit(v)}
                  size="large"
                  sx={{ height: '25px' }}
                >
                  <ToggleButton value="7d" sx={{ '&.Mui-selected': { background: '#C4CAF1', color: '#3747B0' } }}>
                    W
                  </ToggleButton>
                  <ToggleButton value="30d" sx={{ '&.Mui-selected': { background: '#C4CAF1', color: '#3747B0' } }}>
                    M
                  </ToggleButton>
                  <ToggleButton value="90d" sx={{ '&.Mui-selected': { background: '#C4CAF1', color: '#3747B0' } }}>
                    Q
                  </ToggleButton>
                  <ToggleButton value="365d" sx={{ '&.Mui-selected': { background: '#C4CAF1', color: '#3747B0' } }}>
                    Y
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xl={18} xs={8}>
            <ResponsiveGridBox gap={1} display="grid">
              {labels.map((label, idx) => (
                <Grid key={idx} item>
                  <Appbox
                    onClick={() => label !== 'Quotes' && boxFilterFunc(label)}
                    style={{ border: label !== 'Quotes' && boxFilters[label] !== '' ? '1px solid grey' : 'none' }}
                  >
                    <StatsNumberContainer>
                      <StatsNumber>{mappedData[label]}</StatsNumber>
                    </StatsNumberContainer>
                    <LabelContainer>
                      <Typography>{label}</Typography>
                    </LabelContainer>
                  </Appbox>
                </Grid>
              ))}
            </ResponsiveGridBox>
          </Grid>
        </Grid>
        <Typography align="left" fontSize={'12px'} paddingX="16px">
          Data displayed is current as of previous day.
        </Typography>
      </Box>
      <StyledContainer>
        <Grid item lg={12} sm={12} xs={12} s x={{ mt: 2 }}>
          <Grid container spacing={2} height="100%">
            <Grid item lg={6} sm={6} xs={6} display="flex" alignItems="center" justifyContent="start">
              <Typography fontSize={'20px'}>Applications</Typography>
            </Grid>
            <Grid item lg={6} sm={6} xs={6} display="flex" alignItems="center" justifyContent="end">
              <Tooltip
                title={filterBy.productName === 'All' && 'Please select a product to start a new application.'}
                PopperProps={{
                  style: { pointerEvents: 'none', marginTop: 0, marginLeft: 0 },
                }}
                margin="0px 0px 0px 100px"
              >
                <div>
                  <Button
                    sx={{
                      textTransform: 'none',
                      height: '40px',
                      backgroundColor: '#3C4FB9',
                      color: 'white',
                      borderRadius: '100px',
                      mt: '10px',
                      '&:hover': {
                        backgroundColor: '#9BA5DF',
                        color: 'inherit',
                      },
                      '&:disabled': {
                        backgroundColor: '#C4CAF1',
                        color: 'white',
                        textTransform: 'none',
                      },
                    }}
                    disabled={filterBy.productName === 'All' || filterBy.productName === 'Quility Level Term and ROP'}
                    onClick={() => window.open(productInQuestion.productUrl, '_blank')}
                  >
                    + New Application
                  </Button>
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={12} sm={12} xs={12}>
          <Grid container spacing={0}>
            <Grid item lg={3} md={12} sm={12} xs={12} display="flex" alignItems="center" justifyContent="start">
              <TextField
                type="search"
                variant="outlined"
                margin="normal"
                fullWidth
                label="Search for an application"
                value={searchBy}
                placeholder="Enter a policy number or ARC ID"
                style={{
                  background: 'white',
                }}
                onChange={(e) => setSearchBy(e.target.value.trim())}
                onKeyDown={keyDownHandler}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* <StyledFilters> */}
            <Grid
              item
              lg={9}
              md={12}
              sm={12}
              xs={12}
              sx={{
                display: 'flex',
                alignItems: { lg: 'center', md: 'center', sm: 'end', xs: 'end' },
                justifyContent: { lg: 'end', md: 'end', sm: 'start', xs: 'start' },
              }}
            >
              <StyledFilterButtonGrid item lg={2} md={2} display="flex" alignItems="center" justifyContent="end">
                <FilterButton
                  label="Application Status"
                  options={applicationStatusOptions}
                  onFilterChange={(selected) => handleFilterBy('appStatus')(selected)}
                />

                <FilterButton
                  label="Ticket Status"
                  options={ticketStatusOptions}
                  disabled={true}
                  onFilterChange={(selected) => handleFilterBy('ticketStatus')(selected)}
                />
              </StyledFilterButtonGrid>
              <Button
                sx={{
                  fontSize: { lg: '14px', md: '14px', xs: '12px' },
                  width: 'fit-content',
                  height: 'fit-content',
                  fontWeight: '700',
                  textTransform: 'none',
                }}
                onClick={clearAllFilters}
              >
                Clear All Filters
              </Button>
            </Grid>
            {/* </StyledFilters> */}
          </Grid>
        </Grid>
      </StyledContainer>
      <Grid item lg={12} sm={12} xs={12}>
        <Grid container spacing={2} height="100%">
          <Grid item lg={12} md={12} sm={12} xs={12}>
            {loading ? (
              <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
              </Backdrop>
            ) : (
              <ApplicationManagementTable
                rowsData={visibleRows}
                filterBy={filterBy}
                boxFilters={boxFilters}
                products={products}
                productInQuestion={productInQuestion}
                displayingFtapps={displayingFtapps}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export default ApplicationManagement

const appManagementStyle = {
  textField: {
    textAlign: 'left',
    backgroundColor: 'white',
    width: { lg: '238px', md: '238px', sm: '238px', xs: '238px' },
    margin: '5px',
  },
}
